import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import HeroSection from "../../Components/Common/HeroSection";
import { NewsApiUrls } from "../../Api/Auth/ApiUrls";
import useSWRInfinite from "swr/infinite";
import { Link } from "react-router-dom";
import Button from "../../Components/Common/Button";
import { noAuthfetcher } from "../../Api/swrConfig";

const PAGE_SIZE = 6;

const getKey = (pageIndex, previousPageData) => {
  if (previousPageData && !previousPageData.data.length) return null; // reached the end
  return NewsApiUrls.list(`offset=${pageIndex * PAGE_SIZE}&limit=${PAGE_SIZE}`);
};

const NewsArchive = () => {
  const { data, isLoading, setSize, isValidating } = useSWRInfinite(
    getKey,
    noAuthfetcher
  );

  const loadMoreRef = useRef(null);

  const allNews = data ? data.flatMap((page) => page.data) : [];

  const hasMore = data && data[data.length - 1]?.pagination?.hasNextPage;

  // Auto-load when scrolling near bottom using IntersectionObserver
  useEffect(() => {
    if (!hasMore) return;

    const target = loadMoreRef.current; // capture once

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, setSize]);

  return (
    <motion.div
      initial={{ opacity: 0.5, y: -40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <HeroSection
        title={"News Archive"}
        backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        subtitle={"One Platform Many Courses"}
      />

      <div className="container mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
        {!isLoading && allNews.length > 0 ? (
          allNews.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col"
            >
              <img
                src={item.link}
                alt={item.title}
                className="w-full h-60 object-cover"
              />
              <div className="p-6 flex flex-col flex-grow">
                <h2 className="text-2xl font-bold mb-2">{item.title}</h2>
                <p className="text-gray-600 mb-4 line-clamp-3" dangerouslySetInnerHTML={{ __html: item.description }} />
                 
                <div className="mt-auto">
                  <Link to={`/news/${item.slug}`}>
                    <Button>See More</Button>
                  </Link>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center col-span-full text-gray-500">
            {isLoading ? "Loading news..." : "No news available."}
          </div>
        )}
      </div>

      {hasMore && (
        <div ref={loadMoreRef} className="text-center py-4">
          {isValidating ? "Loading more..." : "Scroll to load more..."}
        </div>
      )}
    </motion.div>
  );
};

export default NewsArchive;
